"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import { BadgeCheck, Bookmark, ChevronDown, Layers, LifeBuoy, LogOut, Settings, UserRound, Users, Wallet } from "lucide-react";
import { useAuthStore } from "@/store/useAuthStore";
import { sanitizeUrl } from "@/lib/helpers";
import { initials } from "./tiles";
import { Popover, toast } from "./ui";
import "./account.css";

/**
 * THE ACCOUNT CONTROL IN THE TOP BAR.
 *
 * Signed out it is the Sign in button. Signed in it is the viewer's avatar and
 * name, opening the account menu.
 *
 * THE HYDRATION SLOT IS NOT HERE ANY MORE, AND THAT IS THE POINT. The auth
 * store rehydrates manually (deliberate — it avoids an SSR/CSR mismatch flash
 * on first paint), so `user` is null for the first frame on every visit. That
 * still needs a fixed-width placeholder, but the top bar now shows this
 * control as one cell of a three-cell cluster, and three components each
 * deciding independently when they have hydrated can disagree by a frame.
 * `HomeV2Shell` owns the slot and renders the cluster, this control, or the
 * placeholder — one decision, one owner. See `.v2-cluster`.
 */

export default function AccountMenu(t0) {
  const $ = _c(68);
  const {
    lang
  } = t0;
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  let t2;
  if ($[2] !== lang) {
    t2 = p => `/${lang}${p}`;
    $[2] = lang;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const href = t2;
  const user = useAuthStore(_temp);
  const token = useAuthStore(_temp2);
  const logout = useAuthStore(_temp3);
  if (!user || !token) {
    const t3 = href("/auth/login");
    let t4;
    if ($[4] !== t) {
      t4 = t("\u0648\u0631\u0648\u062F", "Sign in");
      $[4] = t;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== t3 || $[7] !== t4) {
      t5 = <Link className="v2-btn v2-btn--sm v2-focus" href={t3}>{t4}</Link>;
      $[6] = t3;
      $[7] = t4;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  let t3;
  if ($[9] !== user.profile?.cover) {
    t3 = sanitizeUrl(user.profile?.cover) || null;
    $[9] = user.profile?.cover;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const avatar = t3;
  const display = user.name || user.username;
  let T0;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[11] !== avatar || $[12] !== display || $[13] !== href || $[14] !== t || $[15] !== user.blue_check || $[16] !== user.username) {
    const groups = [{
      title: t("\u062D\u0633\u0627\u0628 \u0645\u0646", "My account"),
      items: [{
        href: `/artists/${user.username}`,
        Icon: UserRound,
        label: t("\u067E\u0631\u0648\u0641\u0627\u06CC\u0644", "Profile")
      }, {
        href: "/portfolio",
        Icon: Layers,
        label: t("\u067E\u0648\u0631\u062A\u0641\u0648\u0644\u06CC\u0648", "Portfolio")
      }, {
        href: "/saved",
        Icon: Bookmark,
        label: t("\u0630\u062E\u06CC\u0631\u0647\u200C\u0634\u062F\u0647\u200C\u0647\u0627", "Saved")
      }]
    }, {
      title: t("\u06A9\u06CC\u0641 \u067E\u0648\u0644", "Wallet"),
      items: [{
        href: "/wallet",
        Icon: Wallet,
        label: t("\u06A9\u06CC\u0641 \u067E\u0648\u0644", "Wallet")
      }, {
        href: "/referrals",
        Icon: Users,
        label: t("\u0645\u0639\u0631\u0641\u06CC \u062F\u0648\u0633\u062A\u0627\u0646", "Referrals")
      }]
    }, {
      title: t("\u0628\u06CC\u0634\u062A\u0631", "More"),
      items: [{
        href: "/settings",
        Icon: Settings,
        label: t("\u062A\u0646\u0638\u06CC\u0645\u0627\u062A", "Settings")
      }, {
        href: "/ticket",
        Icon: LifeBuoy,
        label: t("\u067E\u0634\u062A\u06CC\u0628\u0627\u0646\u06CC", "Support")
      }]
    }];
    T0 = Popover;
    t4 = "end";
    t5 = "v2-acct-menu";
    if ($[24] !== t) {
      t6 = t("\u0645\u0646\u0648\u06CC \u062D\u0633\u0627\u0628", "Account menu");
      $[24] = t;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    if ($[26] !== avatar || $[27] !== display) {
      t7 = t10 => {
        const {
          open,
          toggle,
          id
        } = t10;
        return <button type="button" className="v2-acct v2-focus" onClick={toggle} aria-expanded={open} aria-controls={id} data-open={open || undefined}><span className="v2-acct-face">{avatar ? <Image src={avatar} alt="" width={26} height={26} /> : <span className="v2-acct-ini" aria-hidden={true}>{initials(display)}</span>}</span><span className="v2-acct-name">{display}</span><ChevronDown aria-hidden={true} /></button>;
      };
      $[26] = avatar;
      $[27] = display;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    const t10 = href(`/artists/${user.username}`);
    let t11;
    if ($[29] !== avatar || $[30] !== display) {
      t11 = <span className="v2-acct-face v2-acct-face--lg">{avatar ? <Image src={avatar} alt="" width={40} height={40} /> : <span className="v2-acct-ini" aria-hidden={true}>{initials(display)}</span>}</span>;
      $[29] = avatar;
      $[30] = display;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    let t12;
    if ($[32] !== t || $[33] !== user.blue_check) {
      t12 = user.blue_check ? <BadgeCheck aria-label={t("\u062A\u0623\u06CC\u06CC\u062F\u0634\u062F\u0647", "Verified")} /> : null;
      $[32] = t;
      $[33] = user.blue_check;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    let t13;
    if ($[35] !== display || $[36] !== t12) {
      t13 = <span className="v2-acct-display">{display}{t12}</span>;
      $[35] = display;
      $[36] = t12;
      $[37] = t13;
    } else {
      t13 = $[37];
    }
    let t14;
    if ($[38] !== user.username) {
      t14 = <span className="v2-acct-at">@{user.username}</span>;
      $[38] = user.username;
      $[39] = t14;
    } else {
      t14 = $[39];
    }
    let t15;
    if ($[40] !== t13 || $[41] !== t14) {
      t15 = <span className="v2-acct-id">{t13}{t14}</span>;
      $[40] = t13;
      $[41] = t14;
      $[42] = t15;
    } else {
      t15 = $[42];
    }
    if ($[43] !== t10 || $[44] !== t11 || $[45] !== t15) {
      t8 = <Link className="v2-acct-head v2-focus" href={t10}>{t11}{t15}</Link>;
      $[43] = t10;
      $[44] = t11;
      $[45] = t15;
      $[46] = t8;
    } else {
      t8 = $[46];
    }
    t9 = groups.map(g => <div key={g.title}><div className="v2-pop-sep" /><div className="v2-pop-title">{g.title}</div>{g.items.map(t16 => {
        const {
          href: p_0,
          Icon,
          label
        } = t16;
        return <Link className="v2-pop-item" href={href(p_0)} key={p_0} role="menuitem"><Icon aria-hidden={true} /><span>{label}</span></Link>;
      })}</div>);
    $[11] = avatar;
    $[12] = display;
    $[13] = href;
    $[14] = t;
    $[15] = user.blue_check;
    $[16] = user.username;
    $[17] = T0;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    T0 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  let t10;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="v2-pop-sep" />;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  let t11;
  if ($[48] !== logout || $[49] !== t) {
    t11 = () => {
      logout();
      toast({
        tone: "info",
        title: t("\u0627\u0632 \u062D\u0633\u0627\u0628 \u062E\u0627\u0631\u062C \u0634\u062F\u06CC\u062F", "Signed out"),
        body: t("\u0647\u0631 \u0648\u0642\u062A \u062E\u0648\u0627\u0633\u062A\u06CC\u062F \u062F\u0648\u0628\u0627\u0631\u0647 \u0648\u0627\u0631\u062F \u0634\u0648\u06CC\u062F.", "Sign back in whenever you like.")
      });
    };
    $[48] = logout;
    $[49] = t;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  let t12;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <LogOut aria-hidden={true} />;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  let t13;
  if ($[52] !== t) {
    t13 = t("\u062E\u0631\u0648\u062C \u0627\u0632 \u062D\u0633\u0627\u0628", "Sign out");
    $[52] = t;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== t13) {
    t14 = <span>{t13}</span>;
    $[54] = t13;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  let t15;
  if ($[56] !== t11 || $[57] !== t14) {
    t15 = <button type="button" className="v2-pop-item v2-pop-item--danger" role="menuitem" onClick={t11}>{t12}{t14}</button>;
    $[56] = t11;
    $[57] = t14;
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] !== T0 || $[60] !== t15 || $[61] !== t4 || $[62] !== t5 || $[63] !== t6 || $[64] !== t7 || $[65] !== t8 || $[66] !== t9) {
    t16 = <T0 align={t4} className={t5} label={t6} trigger={t7}>{t8}{t9}{t10}{t15}</T0>;
    $[59] = T0;
    $[60] = t15;
    $[61] = t4;
    $[62] = t5;
    $[63] = t6;
    $[64] = t7;
    $[65] = t8;
    $[66] = t9;
    $[67] = t16;
  } else {
    t16 = $[67];
  }
  return t16;
}
function _temp3(s_1) {
  return s_1.logout;
}
function _temp2(s_0) {
  return s_0.token;
}
function _temp(s) {
  return s.user;
}